import { useState } from 'react';
import './ProjectList.css';
import { CDN_CONFIG } from '../../config/cdn';

const ProjectList = ({ projects, onEdit, onDelete, onMove }) => {
  // Function to get the best available image for a project
  const getProjectImage = (project) => {
    // Priority: thumbnail > media > first section media
    const candidates = [
      project.thumbnail,
      project.media,
      project.projectContent?.sections?.[0]?.media,
      project.projectContent?.sections?.[0]?.imageName,
    ].filter(Boolean);

    return candidates[0] || null;
  };

  // Component to handle image with fallbacks
  const ProjectThumbnail = ({ project }) => {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [showPlaceholder, setShowPlaceholder] = useState(false);
    
    // Helper to check if a URL is a video
    const isVideo = (url) => {
      if (!url) return false;
      const videoExtensions = ['.mp4', '.webm', '.mov', '.avi'];
      return videoExtensions.some(ext => url.toLowerCase().includes(ext));
    };

    // Build list of media candidates with their types
    const mediaCandidates = [
      { url: project.thumbnail, isVideo: project.mediaType === 'video' || isVideo(project.thumbnail) },
      { url: project.media, isVideo: project.mediaType === 'video' || isVideo(project.media) },
      ...(project.projectContent?.sections || [])
        .filter(s => s.media || s.imageName)
        .map(s => ({
          url: s.media || s.imageName,
          isVideo: s.type === 'Video' || isVideo(s.media || s.imageName)
        }))
    ].filter(item => item.url);

    const handleError = () => {
      if (currentImageIndex < mediaCandidates.length - 1) {
        // Try next media
        setCurrentImageIndex(currentImageIndex + 1);
      } else {
        // No more media to try, show placeholder
        setShowPlaceholder(true);
      }
    };

    if (showPlaceholder || mediaCandidates.length === 0) {
      return (
        <div className="image-placeholder">
          <span className="placeholder-icon">🖼️</span>
          <span className="placeholder-text">No Image</span>
        </div>
      );
    }

    const currentMedia = mediaCandidates[currentImageIndex];
    
    // Transform video URLs to use CDN
    const mediaUrl = currentMedia.isVideo 
      ? CDN_CONFIG.getVideoFromPath(currentMedia.url)
      : currentMedia.url;

    return currentMedia.isVideo ? (
      <video
        key={currentImageIndex}
        src={mediaUrl}
        muted
        autoPlay
        loop
        playsInline
        onError={handleError}
      />
    ) : (
      <img
        key={currentImageIndex}
        src={mediaUrl}
        alt={project.title}
        onError={handleError}
      />
    );
  };

  return (
    <div className="project-list">
      {projects.length === 0 ? (
        <div className="no-projects">
          <p>No projects yet. Add your first project!</p>
        </div>
      ) : (
        <div className="projects-grid">
          {projects.map((project, index) => (
            <div key={project._id} className="project-card">
              {onMove && (
                <div className="project-reorder">
                  <button
                    onClick={() => onMove(project._id, 'up')}
                    className="reorder-btn"
                    disabled={index === 0}
                    title="Move up"
                  >
                    ↑
                  </button>
                  <span className="order-number">#{index + 1}</span>
                  <button
                    onClick={() => onMove(project._id, 'down')}
                    className="reorder-btn"
                    disabled={index === projects.length - 1}
                    title="Move down"
                  >
                    ↓
                  </button>
                </div>
              )}
              
              <div className="project-thumbnail">
                <ProjectThumbnail project={project} />
              </div>

              <div className="project-info">
                <div className="project-header">
                  <h3>{project.title}</h3>
                  <span className="category-badge">
                    {project.category}
                  </span>
                </div>

                <div className="project-meta">
                  <span className="meta-item">
                    {project.mediaType === 'video' ? '🎥' : '🖼️'} {project.mediaType}
                  </span>
                  <span className="meta-item">
                    📄 {project.projectContent?.sections?.length || 0} sections
                  </span>
                </div>

                <div className="project-actions">
                  <button
                    onClick={() => onEdit(project)}
                    className="action-btn edit-btn"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => onDelete(project._id)}
                    className="action-btn delete-btn"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProjectList;

